import { gutterLeftPx } from '../print-bleed'
import { PRINT_BLEED_TINT_INSIDE_TRIM_PT } from '@/lib/document/print-bleed'

type DoubleSpreadGutterTintAboveShapesProps = {
  bleedWidth: number
  bleedMargin: number
  bleedHeight: number
  trimWidth: number
  trimHeight: number
  centerGap: number
  /**
   * Shared, snapped bindery seam X — same value the ruler "0" tick, spine bar, dashed trim, and
   * margin guides all draw at (`center-canvas/hooks/use-spread-layout.ts`'s
   * `snappedSpreadOriginCanvasX`).
   */
  spineSnapCanvasX: number
  /** Current canvas zoom. Applied here explicitly rather than via an ancestor's CSS `transform`. */
  scale: number
  /** Gates the entire gutter tint — column and adjoining in-trim segments alike — fed from the
   * editor's "Show Bleed Bar" toggle. */
  showOutsideTrim: boolean
  zIndex?: number
}

/**
 * Bindery gutter tint for a double spread: the gutter column itself (the "darker T" band centered
 * on the spine) plus the two in-trim row segments on either side of it, top and bottom. All of
 * these used to live inside `PrintBleedTints` (`print-bleed-tint.tsx`), positioned in
 * document-space units inside `documentCanvasRef`'s own `transform: scale()` tree — same home the
 * solid spine bar used to have.
 *
 * Moved out for the same, live-confirmed reason as the spine bar (see
 * `double-spread-spine-bar-above-shapes.tsx`): the gutter column's computed `left`/`width` were
 * exactly centered on the shared seam, yet the painted band rendered visibly off-center from that
 * box at high zoom — a `transform: scale()` ancestor can rasterize sub-pixel-positioned content at
 * a resolution that doesn't match its own CSS box. Computing final screen pixels directly here,
 * with no CSS transform between this element and its `inset-0` wrapper, removes that ambiguity.
 *
 * The adjoining in-trim segments (`PrintBleedTints`' `topInTrimLeftSegW`/`topInTrimRightSegW` and
 * their bottom counterparts) moved here too, in the same pass, for a concrete reason rather than
 * out of caution: moving only the gutter column first left its edges pixel-perfect while the
 * segments right next to it were still doc-space content inside the scale-wrapper tree — subject
 * to the exact same rasterization drift the gutter column used to have — so the two no longer
 * quite lined up (confirmed live: an overlap on one side, a gap on the other). Computing all of
 * them here together, from the same screen-pixel values, removes that seam. Their *other* edges
 * (against `PrintBleedTints`' outer "wing" bands, which stay where they were) don't need the same
 * treatment: both sides of that boundary are the same single-layer tint color, so a sub-pixel
 * mismatch there is not visible the way a mismatch next to the double-stacked, visibly darker
 * gutter column was.
 */
export function DoubleSpreadGutterTintAboveShapes({
  bleedWidth,
  bleedMargin,
  bleedHeight,
  trimWidth,
  trimHeight,
  centerGap,
  spineSnapCanvasX,
  scale,
  showOutsideTrim,
  zIndex = 3,
}: DoubleSpreadGutterTintAboveShapesProps) {
  if (centerGap <= 0 || trimHeight <= 0) {
    return null
  }

  const insideTint = PRINT_BLEED_TINT_INSIDE_TRIM_PT
  const bottomBandTop = bleedMargin + trimHeight - insideTint
  const trimInnerW = Math.max(0, Math.min(trimWidth, bleedWidth - 2 * bleedMargin))
  const topInnerLeft = bleedMargin
  const inTrimRowRightEdge = topInnerLeft + trimInnerW
  const gutterLeftDoc = gutterLeftPx(bleedMargin, trimWidth, centerGap, spineSnapCanvasX)
  const gutterRightDoc = gutterLeftDoc + centerGap
  const leftSegWDoc =
    trimInnerW > 0 ? Math.max(0, Math.min(gutterLeftDoc, inTrimRowRightEdge) - topInnerLeft) : 0
  const rightSegWDoc =
    trimInnerW > 0 ? Math.max(0, inTrimRowRightEdge - Math.max(gutterRightDoc, topInnerLeft)) : 0
  const rightSegLeftDoc = Math.max(gutterRightDoc, topInnerLeft)

  const leftPx = gutterLeftDoc * scale
  const widthPx = centerGap * scale
  const insideTintPx = insideTint * scale
  const topPx = bleedMargin * scale
  const bottomBandTopPx = bottomBandTop * scale
  const fillStyle = { backgroundColor: 'var(--doc-bleed-tint-outside-trim)' } as const

  return (
    <>
      {showOutsideTrim && (
        <div
          className="pointer-events-none absolute"
          style={{ zIndex, left: leftPx, top: 0, width: widthPx, height: topPx, ...fillStyle }}
        />
      )}
      {showOutsideTrim && (
        <div
          className="pointer-events-none absolute"
          style={{
            zIndex,
            left: leftPx,
            top: topPx,
            width: widthPx,
            height: trimHeight * scale,
            ...fillStyle,
          }}
        />
      )}
      {showOutsideTrim && (
        <div
          className="pointer-events-none absolute"
          style={{
            zIndex,
            left: leftPx,
            top: (bleedMargin + trimHeight) * scale,
            width: widthPx,
            height: bleedHeight * scale - (bleedMargin + trimHeight) * scale,
            ...fillStyle,
          }}
        />
      )}
      {showOutsideTrim && (
        <div
          className="pointer-events-none absolute"
          style={{
            zIndex,
            left: leftPx,
            top: topPx,
            width: widthPx,
            height: insideTintPx,
            ...fillStyle,
          }}
        />
      )}
      {showOutsideTrim && (
        <div
          className="pointer-events-none absolute"
          style={{
            zIndex,
            left: leftPx,
            top: bottomBandTopPx,
            width: widthPx,
            height: insideTintPx,
            ...fillStyle,
          }}
        />
      )}
      {showOutsideTrim && leftSegWDoc > 0 && (
        <>
          <div
            className="pointer-events-none absolute"
            style={{
              zIndex,
              left: topInnerLeft * scale,
              top: topPx,
              width: leftSegWDoc * scale,
              height: insideTintPx,
              ...fillStyle,
            }}
          />
          <div
            className="pointer-events-none absolute"
            style={{
              zIndex,
              left: topInnerLeft * scale,
              top: bottomBandTopPx,
              width: leftSegWDoc * scale,
              height: insideTintPx,
              ...fillStyle,
            }}
          />
        </>
      )}
      {showOutsideTrim && rightSegWDoc > 0 && (
        <>
          <div
            className="pointer-events-none absolute"
            style={{
              zIndex,
              left: rightSegLeftDoc * scale,
              top: topPx,
              width: rightSegWDoc * scale,
              height: insideTintPx,
              ...fillStyle,
            }}
          />
          <div
            className="pointer-events-none absolute"
            style={{
              zIndex,
              left: rightSegLeftDoc * scale,
              top: bottomBandTopPx,
              width: rightSegWDoc * scale,
              height: insideTintPx,
              ...fillStyle,
            }}
          />
        </>
      )}
    </>
  )
}
